import React, { useState } from "react";
import { MdAutoFixHigh, MdRefresh, MdCheckCircle, MdInbox } from "react-icons/md";
import {
  applyEstimates,
  getEstimatePreview,
} from "../../../../services/capacityApi";
import {
  ApplyEstimatesResult,
  EstimateAllocationPreviewItem,
} from "../../../../interfaces/Capacity";

interface Option {
  id: number;
  name: string;
}

interface Props {
  projects: Option[];
  onChanged: () => void;
}

const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("pt-BR");

const EstimateBridge: React.FC<Props> = ({ projects, onChanged }) => {
  const [projectId, setProjectId] = useState<string>("");
  const [overwrite, setOverwrite] = useState(true);
  const [preview, setPreview] = useState<EstimateAllocationPreviewItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<ApplyEstimatesResult | null>(null);
  const [loaded, setLoaded] = useState(false);

  const loadPreview = async () => {
    if (!projectId) return;
    setLoading(true);
    setResult(null);
    try {
      const data = await getEstimatePreview(Number(projectId), undefined, 9);
      setPreview(data);
      setLoaded(true);
    } catch (e) {
      console.error("Erro ao carregar preview", e);
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!projectId) return;
    setApplying(true);
    try {
      const res = await applyEstimates({
        projectId: Number(projectId),
        weeks: 9,
        overwrite,
      });
      setResult(res);
      onChanged();
      await loadPreview();
    } catch (e) {
      console.error("Erro ao aplicar estimativas", e);
    } finally {
      setApplying(false);
    }
  };

  const totalEstimated = preview.reduce((acc, p) => acc + p.estimatedHours, 0);

  return (
    <section className="dv-aura rounded-2xl border border-dv-blue-100 bg-dv-blue-50/40 p-4 dark:border-dv-blue-500/20 dark:bg-navy-900/40">
      <div className="mb-2 flex items-center gap-2">
        <MdAutoFixHigh className="text-dv-blue-600 dark:text-dv-cyan-300" size={20} aria-hidden="true" />
        <h4 className="text-sm font-bold text-navy-700 dark:text-white">
          Gerar alocações a partir das estimativas
        </h4>
      </div>
      <p className="mb-3 text-xs text-gray-700 dark:text-gray-300">
        Converte a estimativa de cada tarefa (responsável × prazo) em alocação semanal automaticamente.
        A estimativa é distribuída entre as semanas que a tarefa atravessa.
      </p>

      <div className="flex flex-col flex-wrap items-stretch gap-2.5 sm:flex-row sm:items-end">
        <div className="flex w-full flex-col sm:w-56">
          <label htmlFor="estimate-bridge-project" className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-300">
            Projeto <span className="text-red-500">*</span>
          </label>
          <select
            id="estimate-bridge-project"
            className={inputCls}
            value={projectId}
            onChange={(e) => {
              setProjectId(e.target.value);
              setLoaded(false);
              setPreview([]);
              setResult(null);
            }}
          >
            <option value="">Projeto…</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 sm:pb-2.5">
          <input
            type="checkbox"
            checked={overwrite}
            onChange={(e) => setOverwrite(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-dv-blue-600 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900"
          />
          Sobrescrever alocações existentes
        </label>

        <button
          type="button"
          onClick={loadPreview}
          disabled={!projectId || loading}
          className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
        >
          <MdRefresh size={16} aria-hidden="true" /> {loading ? "Carregando..." : "Pré-visualizar"}
        </button>

        <button
          type="button"
          onClick={apply}
          disabled={!projectId || applying || preview.length === 0}
          className="dv-sheen flex items-center justify-center gap-1.5 rounded-xl bg-dv-gradient px-5 py-2.5 text-sm font-bold text-white shadow-dv-glow transition-all active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <MdAutoFixHigh size={16} aria-hidden="true" /> {applying ? "Gerando..." : "Gerar alocações"}
        </button>
      </div>

      {result && (
        <div className="mt-3 flex items-center gap-1.5 rounded-xl bg-green-50 px-4 py-2 text-sm font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300">
          <MdCheckCircle size={16} aria-hidden="true" /> {result.created} criada(s), {result.updated} atualizada(s)
          {result.skippedExisting > 0 ? `, ${result.skippedExisting} mantida(s)` : ""}.
        </div>
      )}

      {loaded && (
        <div className="mt-3">
          {preview.length === 0 ? (
            <div className="flex items-center gap-2 rounded-xl border border-dashed border-gray-200 px-4 py-6 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-300">
              <MdInbox size={18} aria-hidden="true" className="shrink-0 text-gray-400" />
              Nenhuma demanda estimada para este projeto. Adicione estimativa, responsável e prazo às tarefas.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-gray-100 bg-white nina-surface shadow-dv-md dark:border-white/10 dark:bg-navy-800">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-navy-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Colaborador</th>
                    <th className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Semana</th>
                    <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Estimado</th>
                    <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">Já alocado</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((p, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-navy-700">
                      <td className="px-4 py-3 font-medium text-navy-700 dark:text-white">
                        {p.employeeName}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{fmtDate(p.weekStart)}</td>
                      <td className="px-4 py-3 text-right font-semibold tabular-nums text-dv-blue-600 dark:text-dv-cyan-300">
                        {p.estimatedHours}h
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-300">
                        {p.currentAllocatedHours}h
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold dark:bg-navy-900">
                    <td className="px-4 py-3 text-navy-700 dark:text-white" colSpan={2}>
                      Total estimado
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-dv-blue-600 dark:text-dv-cyan-300">
                      {Math.round(totalEstimated * 100) / 100}h
                    </td>
                    <td className="px-4 py-3"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default EstimateBridge;
